import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Joyride from "react-joyride";
import { MapContext, MapActions } from "context/map";
import CheckBox from "components/CheckBox";
import "./index.scss";
import Button from "components/Button";

const CustomTooltip = ({
  step,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => {
  const handleSkipNextVisit = () => {
    if (localStorage.getItem("skip-popover-tutorial") === "1") {
      localStorage.setItem("skip-popover-tutorial", "0");
    } else {
      localStorage.setItem("skip-popover-tutorial", "1");
    }
  };

  return (
    <div {...tooltipProps} className="custom-tooltip">
      {step.title && <h4 className="title">{step.title}</h4>}
      <p className="description">{step.content}</p>
      <div className="actions">
        {!isLastStep && (
          <Button
            label="Skip Tutorial"
            color="black"
            type="outlined"
            {...skipProps}
          />
        )}
        {isLastStep ? (
          <button className="last" type="button" {...primaryProps}>
            <span id="last">Okay, I&apos;ll try it myself</span>
          </button>
        ) : (
          <Button
            label="Next"
            color="green"
            type="solid"
            block
            {...primaryProps}
          />
        )}
      </div>
      {isLastStep && (
        <label className="skip-next-visit">
          <CheckBox onChange={handleSkipNextVisit} />
          Skip this the next time I visit the page
        </label>
      )}
    </div>
  );
};

CustomTooltip.propTypes = {
  step: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }).isRequired,
  skipProps: PropTypes.shape({
    "aria-label": PropTypes.string.isRequired,
    "data-action": PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
  primaryProps: PropTypes.shape({
    "aria-label": PropTypes.string.isRequired,
    "data-action": PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
  tooltipProps: PropTypes.shape({
    "aria-modal": PropTypes.bool.isRequired,
    role: PropTypes.string.isRequired,
    ref: PropTypes.func.isRequired,
  }).isRequired,
  isLastStep: PropTypes.bool.isRequired,
};

const Tutorial = () => {
  const { state, dispatch } = useContext(MapContext);
  const { tutorialMode, gettingStarted } = state;
  const [steps, setSteps] = useState([]);
  const [items] = useState([
    {
      target: "step-1",
      title: "Select your fields",
      description:
        "You can select your fields by clicking on this button or choosing from fields on your mapping screen.",
    },
    {
      target: "step-2",
      title: "Select layers to apply",
      description:
        "From crops to soil, you can apply various types of layers to help you understand and manage your fields better.",
    },
    {
      target: "step-3",
      title: "Choose your desired date",
      description: "Select the date of the layer you wish to view.",
    },
    {
      target: "step-4",
      title: "Select Calculation",
      description:
        "Calculations are state-of-the-art technology which uses satellite imagery to provide you information about your fields.",
    },
    {
      target: "step-5",
      title: "List of actions you can do",
      description: "You can order high resolution imagery for your farm.",
    },
  ]);

  useEffect(() => {
    const newSteps = items.map((item) => {
      return {
        target: `[data-tour="${item.target}"]`,
        title: item.title,
        content: item.description,
        disableBeacon: true,
      };
    });

    setSteps(newSteps);
  }, [items]);

  const onClose = (data) => {
    if (data.status === "skipped") {
      localStorage.setItem("skip-popover-tutorial", "1");
    }

    if (data.status === "ready") {
      dispatch({
        type: MapActions.SET_TUTORIAL_MODE,
        value: false,
      });
    }
  };

  if (!tutorialMode || gettingStarted) return null;

  return (
    <Joyride
      steps={steps}
      continuous
      hideBackButton
      hideCloseButton
      showSkipButton
      disableOverlayClose
      floaterProps={{
        disableAnimation: true,
      }}
      styles={{
        options: {
          arrowColor: "#292b33",
        },
      }}
      tooltipComponent={CustomTooltip}
      callback={onClose}
    />
  );
};

export default Tutorial;
